import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import {
  Button,
  NumberInput,
  RadioButton,
  RadioButtonGroup,
  SkeletonPlaceholder,
} from '@carbon/react';
import { Download, CenterCircle } from '@carbon/react/icons';
import { WellAnalysisChart } from './well-analysis-chart';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  DeclineCurveAnalysisScenarioDropdown,
  type DeclineCurveAnalysisScenario,
} from 'components/well-page-content/decline-curve-analysis/decline-curve-analysis-scenario-dropdown';
import { ChartActions } from './chart-actions';
import {
  dcaScenariosAtom,
  selectedDcaScenarioAtom,
  resetDcaModalStateAtom,
} from 'components/well-page-content/atoms';
import { MineralType } from 'types/minerals';
import { XAxisType } from 'components/well-page-content/types';
import type { DcaWorkspaceWellApi } from 'types/dca-workspace-wells-api/responses';
import { AtomisedNotifications } from 'components/atomised-notifications';
import {
  clearNotificationGroupAtom,
  NotificationGroup,
} from 'components/atomised-notifications/atoms';
import {
  batchRegressionMutation,
  batchRegressionStatusAtom,
  dcaModalForecastSavedAtom,
  isWellSelected,
  setActiveRegressionRequestAtom,
  buildWellSelectionQueryFilter,
  type WellSelectionState,
} from './atoms';

import { useBulkChartVolumes, useBulkDailyProduction } from './hooks';
import { useWorkspaceDcaScenario } from './dca-scenario-hooks';
import { isDailyProductionSource } from 'components/production-source-dropdown';
import type { VolumeDisplayMode } from 'components/well-page-content';
import styles from './decline-curve-analysis.module.scss';
import { VisibleWellSpec } from './types';
import { DcaModal } from './dca-modal';

const CHART_TYPES = [
  { value: 'monthly', label: 'Monthly Volumes' },
  { value: 'logarithmic', label: 'Log-Log' },
  { value: 'cumulative', label: 'Cumulative Volumes' },
  { value: 'ratio', label: 'Ratios' },
] as const;

const MIN_LAYOUT = 1;
const MAX_LAYOUT = 10;
// The title band plus the chart container's top and bottom borders. Only used
// to estimate a row's height for the virtualizer, so it need not be exact.
const CHART_HEADER_HEIGHT = 35;
const GRID_GAP = 16; // matches $spacing-05

/** Width-to-height ratio every chart canvas keeps, the same as the tearsheet. */
const CHART_ASPECT_RATIO = 2;

/** Canvas height used until the grid has been measured. */
const FALLBACK_CHART_CANVAS_HEIGHT = 300;

interface DeclineCurveAnalysisProps {
  wells: DcaWorkspaceWellApi[];
  dcaWorkspaceId: number;
  /** The workspace's saved DCA scenario, selected on load instead of "pdp default". */
  defaultDcaScenarioId?: number | null;
  wellsMap: WellSelectionState;
  loading?: boolean;
  onWellSelectedChange: (uwi: string, selected: boolean) => void;
  onVisibleUwisChange?: (uwis: Set<string>) => void;
  refetchWells: () => void;
}

export const DeclineCurveAnalysis = ({
  wells,
  dcaWorkspaceId,
  defaultDcaScenarioId,
  wellsMap,
  loading = false,
  onWellSelectedChange,
  onVisibleUwisChange,
}: DeclineCurveAnalysisProps) => {
  const [selectedUwi, setSelectedUwi] = useState<string | null>(null);
  const [dcaModalOpen, setDcaModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [selectedChartType, setSelectedChartType] =
    useState<(typeof CHART_TYPES)[number]['value']>('monthly');
  const [rows, setRows] = useState(2);
  const [columns, setColumns] = useState(3);
  const dcaScenariosState = useAtomValue(dcaScenariosAtom);
  const [selectedScenario, setSelectedScenario] = useAtom(
    selectedDcaScenarioAtom
  );

  const wellsByUwi = useMemo(() => {
    const map: Record<string, DcaWorkspaceWellApi> = {};
    return wells.reduce((acc, well) => {
      acc[well.uwi] = well;
      return acc;
    }, map);
  }, [wells]);

  const [selectedLegend, setSelectedLegend] = useState<
    Array<MineralType | 'GOR' | 'WOR' | 'WGR' | 'CGR'>
  >(['oil', 'water', 'gas', 'GOR', 'WOR', 'WGR', 'CGR']);

  const [showTypeCurve, setShowTypeCurve] = useState(false);
  const [volumeDisplayMode, setVolumeDisplayMode] =
    useState<VolumeDisplayMode>('monthly');
  const [xAxisType, setXAxisType] = useState<XAxisType>(
    selectedChartType === 'logarithmic' ? 'count' : 'date'
  );
  const [batchRegression, setBatchRegression] = useAtom(
    batchRegressionMutation
  );
  const regressionStatus = useAtomValue(batchRegressionStatusAtom);
  const setActiveRequest = useSetAtom(setActiveRegressionRequestAtom);
  const clearNotificationGroup = useSetAtom(clearNotificationGroupAtom);
  const setDcaModalSaved = useSetAtom(dcaModalForecastSavedAtom);
  const resetDcaModalState = useSetAtom(resetDcaModalStateAtom);

  const handleForecastSaved = useCallback(() => {
    if (selectedUwi) setDcaModalSaved(selectedUwi);
  }, [selectedUwi, setDcaModalSaved]);

  // The tearsheet's arrows walk `wells`, which is already in the wells table's
  // display order. Switching wells resets the editor's per-well state so the
  // accordions repopulate from the new well's active forecast.
  const selectedWellIndex = useMemo(
    () => (selectedUwi ? wells.findIndex((w) => w.uwi === selectedUwi) : -1),
    [wells, selectedUwi]
  );

  const goToWell = useCallback(
    (index: number) => {
      const next = wells[index];
      if (!next) return;
      resetDcaModalState();
      setSelectedUwi(next.uwi);
    },
    [wells, resetDcaModalState]
  );

  const handleChartTypeChange = useCallback(
    (value: string | number | undefined) => {
      unstable_batchedUpdates(() => {
        const newType = value as (typeof CHART_TYPES)[number]['value'];
        setSelectedChartType(newType);
        setXAxisType(newType === 'logarithmic' ? 'count' : 'date');
      });
    },
    []
  );

  useWorkspaceDcaScenario(defaultDcaScenarioId, dcaWorkspaceId);

  const scenarios: DeclineCurveAnalysisScenario[] = useMemo(() => {
    if (dcaScenariosState.result?.type !== 'success') return [];
    return dcaScenariosState.result.data.results.map((s) => ({
      ...s,
      label: s.name,
    }));
  }, [dcaScenariosState.result]);

  const handleScenarioSelect = useCallback(
    (scenario: DeclineCurveAnalysisScenario | null) => {
      setSelectedScenario(scenario);
    },
    [setSelectedScenario]
  );

  const handleScenarioLaunch = useCallback(
    (scenario: DeclineCurveAnalysisScenario) => {
      window.open(`/dca_scenarios/${scenario.id}`, '_blank');
    },
    []
  );

  const handleLegendClick = useCallback((legend: MineralType) => {
    setSelectedLegend((prev) => {
      if (prev.includes(legend)) {
        return prev.filter((l) => l !== legend);
      }
      return [...prev, legend];
    });
  }, []);

  const uwis = useMemo(() => wells.map((w) => w.uwi), [wells]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // The rows are virtualised, so their height has to be known rather than
  // measured after the fact. Each cell's width comes from the grid's own width
  // divided by the column count, and the canvas takes half of that.
  const [gridWidth, setGridWidth] = useState<number | null>(null);
  // The grid only renders once there are wells, which is after this component
  // first mounts, so the observer is attached from a ref callback rather than
  // an effect that would find nothing to observe.
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);

  const setScrollContainer = useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node;
    setGridEl(node);
  }, []);

  useEffect(() => {
    if (!gridEl) return;

    const observer = new ResizeObserver((entries) => {
      setGridWidth(entries[entries.length - 1].contentRect.width);
    });
    observer.observe(gridEl);

    return () => observer.disconnect();
  }, [gridEl]);

  const chartCanvasHeight = useMemo(() => {
    if (gridWidth === null) return FALLBACK_CHART_CANVAS_HEIGHT;

    const cellWidth = (gridWidth - GRID_GAP * (columns - 1)) / columns;
    if (!(cellWidth > 0)) return FALLBACK_CHART_CANVAS_HEIGHT;

    return Math.round(cellWidth / CHART_ASPECT_RATIO);
  }, [gridWidth, columns]);

  const chartRowHeight = chartCanvasHeight + CHART_HEADER_HEIGHT;

  const totalChartRows = Math.ceil(uwis.length / columns);
  const rowHeight = chartRowHeight + GRID_GAP;
  const displayedRows = Math.min(rows, totalChartRows);
  const viewportHeight =
    displayedRows * chartRowHeight + Math.max(0, displayedRows - 1) * GRID_GAP;

  const rowVirtualizer = useVirtualizer({
    count: totalChartRows,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => rowHeight,
    overscan: 1,
  });

  // The virtualizer caches what estimateSize returned, so a new row height only
  // takes effect once it is told to measure again.
  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowVirtualizer, rowHeight]);

  const virtualItems = rowVirtualizer.getVirtualItems();

  const visibleSpecs = useMemo<VisibleWellSpec[]>(
    () =>
      virtualItems.flatMap((vRow) => {
        const start = vRow.index * columns;
        const end = Math.min(start + columns, uwis.length);
        const specs: VisibleWellSpec[] = [];
        for (let i = start; i < end; i++) {
          const uwi = uwis[i];
          if (!uwi) continue;
          specs.push({
            uwi,
            dcaForecastId: wellsMap.items[uwi]?.dcaForecastId ?? '',
          });
        }
        return specs;
      }),
    [virtualItems, columns, uwis, wellsMap]
  );

  const monthlyChartDataMap = useBulkChartVolumes(
    visibleSpecs,
    selectedScenario?.id?.toString() ?? null
  );

  // On the Daily display, wells whose production comes from a daily source
  // draw it one row per day; the rest show monthly volumes as daily rates.
  const dailySourceUwis = useMemo(
    () =>
      volumeDisplayMode === 'daily'
        ? visibleSpecs
            .map((spec) => spec.uwi)
            .filter((uwi) => {
              const source = monthlyChartDataMap[uwi]?.production.source;
              return source !== undefined && isDailyProductionSource(source);
            })
        : [],
    [volumeDisplayMode, visibleSpecs, monthlyChartDataMap]
  );
  const dailyProductionByUwi = useBulkDailyProduction(dailySourceUwis);
  const chartDataMap = useMemo(() => {
    const merged = { ...monthlyChartDataMap };
    for (const uwi of dailySourceUwis) {
      const daily = dailyProductionByUwi[uwi];
      if (!daily || !merged[uwi]) continue;
      merged[uwi] = {
        ...merged[uwi],
        production: { ...daily, source: merged[uwi].production.source },
      };
    }
    return merged;
  }, [monthlyChartDataMap, dailySourceUwis, dailyProductionByUwi]);

  useEffect(() => {
    if (!onVisibleUwisChange) return;
    const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;
    const viewportBottom = scrollTop + viewportHeight;
    const visible = new Set<string>();
    for (const vRow of virtualItems) {
      if (vRow.start >= viewportBottom || vRow.end <= scrollTop) continue;
      const visibleTop = Math.max(vRow.start, scrollTop);
      const visibleBottom = Math.min(vRow.end, viewportBottom);
      const visibleFraction = (visibleBottom - visibleTop) / vRow.size;
      if (visibleFraction < 0.5) continue;
      const start = vRow.index * columns;
      const end = Math.min(start + columns, uwis.length);
      for (let i = start; i < end; i++) {
        visible.add(uwis[i]);
      }
    }
    onVisibleUwisChange(visible);
  }, [virtualItems, columns, uwis, viewportHeight, onVisibleUwisChange]);

  const normalizedSelectedSeries = useMemo(() => {
    if (selectedChartType === 'ratio') {
      return selectedLegend
        .filter(
          (item): item is 'GOR' | 'WOR' | 'WGR' | 'CGR' =>
            item === 'GOR' || item === 'WOR' || item === 'WGR' || item === 'CGR'
        )
        .map((item) => item.toLowerCase() as 'gor' | 'wor' | 'wgr' | 'cgr');
    }
    return selectedLegend.filter(
      (item): item is MineralType =>
        item === 'oil' || item === 'water' || item === 'gas'
    );
  }, [selectedLegend, selectedChartType]);

  const handleBatchRegression = () => {
    if (!selectedScenario?.id) return;

    const payload: {
      dcaScenarioId: number;
      excludeUwis?: string[];
      uwis?: string[];
    } = {
      dcaScenarioId: selectedScenario.id,
      ...buildWellSelectionQueryFilter(wellsMap),
    };

    clearNotificationGroup(NotificationGroup.DcaRegression);
    setBatchRegression({
      type: 'POST',
      url: `/dca_workspaces/${dcaWorkspaceId}/regression_requests`,
      data: payload,
      onSuccess: (data) => {
        setActiveRequest(data);
      },
    });
  };

  return (
    <Accordion expanded={expanded} onClick={() => setExpanded(!expanded)}>
      <AccordionSummary>
        <h2 className={styles.sectionHeading}>DCA - Decline Curve Analysis</h2>
      </AccordionSummary>
      <AccordionDetails>
        <div className={styles.declineCurveAnalysisContainer}>
          <AtomisedNotifications groupIds={[NotificationGroup.DcaRegression]} />

          <div className={styles.toolbar}>
            <div className={styles.chartTypeSelector}>
              <RadioButtonGroup
                legendText=""
                name="chart-type"
                orientation="horizontal"
                valueSelected={selectedChartType}
                onChange={handleChartTypeChange}
              >
                {CHART_TYPES.map(({ value, label }) => (
                  <RadioButton
                    key={value}
                    id={`chart-type-${value}`}
                    value={value}
                    labelText={label}
                  />
                ))}
              </RadioButtonGroup>
            </div>

            <div className={styles.scenarioDropdown}>
              <DeclineCurveAnalysisScenarioDropdown
                scenarios={scenarios}
                selectedScenario={
                  selectedScenario
                    ? { ...selectedScenario, label: selectedScenario.name }
                    : null
                }
                onScenarioSelect={handleScenarioSelect}
                onScenarioLaunch={handleScenarioLaunch}
                isLoading={dcaScenariosState.loading}
                title=""
              />
            </div>

            <div className={styles.layoutGroup}>
              <div className={styles.layoutInputWrapper}>
                <span className={styles.layoutInputLabel}>Rows</span>
                <NumberInput
                  id="layout-rows"
                  light
                  size="lg"
                  min={MIN_LAYOUT}
                  max={MAX_LAYOUT}
                  value={rows}
                  disabled={uwis.length === 0}
                  onChange={(
                    _e: unknown,
                    { value }: { value: string | number }
                  ) =>
                    setRows(
                      Math.min(MAX_LAYOUT, Math.max(MIN_LAYOUT, Number(value)))
                    )
                  }
                  className={styles.layoutInput}
                  hideSteppers
                />
              </div>
              <div className={styles.layoutInputWrapper}>
                <span className={styles.layoutInputLabel}>Columns</span>
                <NumberInput
                  id="layout-columns"
                  size="lg"
                  light
                  min={MIN_LAYOUT}
                  max={MAX_LAYOUT}
                  value={columns}
                  disabled={uwis.length === 0}
                  onChange={(
                    _e: unknown,
                    { value }: { value: string | number }
                  ) =>
                    setColumns(
                      Math.min(MAX_LAYOUT, Math.max(MIN_LAYOUT, Number(value)))
                    )
                  }
                  className={styles.layoutInput}
                  hideSteppers
                />
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                kind="tertiary"
                size="sm"
                renderIcon={CenterCircle}
                disabled={
                  uwis.length === 0 ||
                  !selectedScenario?.id ||
                  batchRegression.loading ||
                  regressionStatus === 'loading'
                }
                onClick={handleBatchRegression}
              >
                Fit
              </Button>
              <Button kind="primary" size="sm" renderIcon={Download} disabled>
                Export
              </Button>
            </div>
          </div>

          {uwis.length === 0 && loading && (
            <div className={styles.chartsSkeleton} data-columns={columns}>
              {Array.from({ length: rows * columns }).map((_, i) => (
                <SkeletonPlaceholder
                  key={i}
                  className={styles.chartSkeletonCell}
                />
              ))}
            </div>
          )}

          {uwis.length === 0 && !loading && (
            <p className={styles.emptyMessage}>
              No wells to display. Adjust filters or add wells to the workspace.
            </p>
          )}

          {uwis.length > 0 && (
            <>
              <div
                ref={setScrollContainer}
                className={styles.chartsScrollContainer}
                style={{ height: viewportHeight }}
              >
                <div
                  className={styles.chartsVirtualContent}
                  style={{ height: rowVirtualizer.getTotalSize() }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const startIdx = virtualRow.index * columns;
                    const rowUwis = uwis.slice(startIdx, startIdx + columns);

                    return (
                      <div
                        key={virtualRow.index}
                        className={styles.chartsGridRow}
                        data-columns={columns}
                        style={{
                          height: chartRowHeight,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {rowUwis.map((uwi, colIdx) => {
                          const globalIdx = startIdx + colIdx;
                          const wellInfo = wellsByUwi[uwi];
                          return (
                            <div key={globalIdx} className={styles.chartCell}>
                              <WellAnalysisChart
                                key={globalIdx}
                                uwi={uwi}
                                chartData={chartDataMap[uwi]}
                                chartType={selectedChartType}
                                selectedSeries={normalizedSelectedSeries}
                                wellName={wellInfo?.wellName ?? undefined}
                                // Now accounts for the `all_selected` / `not_selected` modes.
                                selected={isWellSelected(wellsMap, uwi)}
                                showTypeCurve={showTypeCurve}
                                xAxisType={xAxisType}
                                volumeDisplayMode={volumeDisplayMode}
                                productionVolumeIsDailyRate={
                                  !!dailyProductionByUwi[uwi] &&
                                  dailySourceUwis.includes(uwi)
                                }
                                height={chartCanvasHeight}
                                onSelectedChange={onWellSelectedChange}
                                onEdit={() => {
                                  setSelectedUwi(uwi);
                                  setDcaModalOpen(true);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              <ChartActions
                selectedLegend={selectedLegend}
                handleLegendClick={handleLegendClick}
                showTypeCurve={showTypeCurve}
                setShowTypeCurve={setShowTypeCurve}
                enableTypeCurve
                chartType={selectedChartType}
                xAxisType={xAxisType}
                setXAxisType={setXAxisType}
                volumeDisplayMode={volumeDisplayMode}
                onVolumeDisplayModeChange={setVolumeDisplayMode}
              />
            </>
          )}
        </div>

        <DcaModal
          uwi={selectedUwi}
          wellName={selectedUwi ? wellsByUwi[selectedUwi]?.wellName : null}
          dcaForecastId={
            selectedUwi
              ? (wellsMap.items[selectedUwi]?.dcaForecastId ?? null)
              : null
          }
          dcaWorkspaceId={dcaWorkspaceId}
          open={dcaModalOpen}
          wellPosition={
            selectedWellIndex >= 0
              ? { index: selectedWellIndex + 1, total: wells.length }
              : undefined
          }
          onPreviousWell={
            selectedWellIndex > 0
              ? () => goToWell(selectedWellIndex - 1)
              : undefined
          }
          onNextWell={
            selectedWellIndex >= 0 && selectedWellIndex < wells.length - 1
              ? () => goToWell(selectedWellIndex + 1)
              : undefined
          }
          onClose={() => {
            setDcaModalOpen(false);
            setSelectedUwi(null);
            resetDcaModalState();
          }}
          onForecastSaved={handleForecastSaved}
        />
      </AccordionDetails>
    </Accordion>
  );
};
